/**
 * ArtinSmartRealty V4 — Premium Luxury Landing Page
 * QA/UX Senior Level Design | PAS Psychology | Full Subscription Pricing
 * Designer: Antigravity AI | Standard: Fortune-500 SaaS
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, BarChart3, MessageSquare, Zap, Shield, CheckCircle,
  ArrowRight, Play, Users, TrendingUp, Target, Phone, MapPin,
  CreditCard, Send, Star, ChevronDown, Globe, Lock, Cpu,
  Menu, X, Check, Minus, Sparkles, Award, Clock, Building2,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── Scroll-reveal hook ──────────────────────────────────────────────────────
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Animated counter ────────────────────────────────────────────────────────
function useCounter(target, duration = 2000, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return val;
}

// ─── Checkout helper ─────────────────────────────────────────────────────────
async function startCheckout(planKey, navigate) {
  const token = localStorage.getItem('token');
  if (!token) {
    navigate('/login', { state: { redirect: '/', plan: planKey } });
    return;
  }
  try {
    const res = await fetch(`${API_BASE}/api/v1/hunter/subscription/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ plan_key: planKey }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Checkout failed');
    }
    const { checkout_url } = await res.json();
    window.location.href = checkout_url;
  } catch (e) {
    alert('Payment error: ' + e.message);
  }
}

// ─── Feature row ─────────────────────────────────────────────────────────────
const Feat = ({ icon: Icon, text, color = 'gold' }) => {
  const colors = {
    gold:  'bg-[#D4A94E]/10 text-[#D4A94E]',
    blue:  'bg-blue-500/10 text-blue-400',
    green: 'bg-green-500/10 text-green-400',
  };
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="text-gray-300 text-sm">{text}</span>
    </div>
  );
};

// ─── Stat counter card ───────────────────────────────────────────────────────
const StatCard = ({ value, suffix = '', label, active }) => {
  const num = parseInt(value.replace(/\D/g, '')) || 0;
  const counted = useCounter(num, 1800, active);
  const display = isNaN(num) || num === 0 ? value : counted + (suffix || value.replace(/\d/g,''));
  return (
    <div className="text-center py-4">
      <p className="text-3xl sm:text-4xl font-black text-white mb-1 tabular-nums">{display}</p>
      <p className="text-gray-500 text-xs sm:text-sm">{label}</p>
    </div>
  );
};

// ─── Pricing toggle ──────────────────────────────────────────────────────────
const periods = [
  { key: 'monthly',  label: 'Monthly',  badge: null },
  { key: 'biannual', label: '6-Month',  badge: '2 months FREE' },
  { key: 'annual',   label: 'Annual',   badge: '3 months FREE' },
];

const hunterPrices = {
  monthly:  { aed: 500,   planKey: 'hunter_monthly',  saving: null },
  biannual: { aed: 2000,  planKey: 'hunter_biannual', saving: 'Save AED 1,000' },
  annual:   { aed: 4500,  planKey: 'hunter_annual',   saving: 'Save AED 1,500' },
};

const crmPrices = {
  monthly:  { aed: 2500,  planKey: 'crm_monthly',   saving: null },
  biannual: { aed: 10000, planKey: 'crm_biannual',  saving: 'Save AED 5,000' },
  annual:   { aed: 22500, planKey: 'crm_annual',    saving: 'Save AED 7,500' },
};

// ─── Pricing card ────────────────────────────────────────────────────────────
const PricingCard = ({
  product, icon: Icon, color, period,
  prices, features, badge, navigate,
}) => {
  const [loading, setLoading] = useState(false);
  const info = prices[period];
  const isAnnual = period === 'annual';

  const handleBuy = async () => {
    setLoading(true);
    await startCheckout(info.planKey, navigate);
    setLoading(false);
  };

  const monthlyEquiv = period === 'monthly'
    ? info.aed
    : period === 'biannual'
      ? Math.round(info.aed / 6)
      : Math.round(info.aed / 12);

  return (
    <div className={`relative rounded-3xl p-7 flex flex-col transition-all duration-300 group
      ${isAnnual
        ? `bg-gradient-to-b from-[#D4A94E]/12 via-[#0a0f1e] to-[#0a0f1e]
           border border-[#D4A94E]/40 shadow-2xl shadow-[#D4A94E]/10
           ring-1 ring-[#D4A94E]/20`
        : 'bg-white/[0.04] border border-white/[0.08] hover:border-white/20'
      }`}>

      {/* Popular badge */}
      {isAnnual && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#D4A94E] to-amber-400
            text-[#050810] text-xs font-black px-5 py-1.5 rounded-full shadow-lg whitespace-nowrap">
            <Sparkles className="w-3 h-3" />
            BEST VALUE
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0
          ${color === 'gold' ? 'bg-[#D4A94E]/15' : 'bg-blue-500/15'}`}>
          <Icon className={`w-5 h-5 ${color === 'gold' ? 'text-[#D4A94E]' : 'text-blue-400'}`} />
        </div>
        <div>
          <p className="text-white font-bold text-sm">{product}</p>
          {badge && (
            <span className="inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-md
              bg-green-500/15 text-green-400 border border-green-500/20">
              {badge}
            </span>
          )}
        </div>
        {info.saving && (
          <div className="ml-auto text-right">
            <span className="text-[10px] font-bold text-green-400 bg-green-500/10
              border border-green-500/20 px-2 py-1 rounded-lg">
              {info.saving}
            </span>
          </div>
        )}
      </div>

      {/* Price */}
      <div className="mb-2">
        <div className="flex items-end gap-2">
          <span className="text-4xl font-black text-white tabular-nums">
            AED {info.aed.toLocaleString()}
          </span>
        </div>
        <p className="text-gray-500 text-xs mt-1">
          {period === 'monthly'
            ? 'per month · cancel anytime'
            : period === 'biannual'
              ? `billed once · ≈ AED ${monthlyEquiv}/mo · 6 months access`
              : `billed once · ≈ AED ${monthlyEquiv}/mo · 12 months access`
          }
        </p>
      </div>

      <div className="h-px bg-white/[0.06] my-5" />

      {/* Features */}
      <div className="space-y-0 flex-1 mb-6">
        {features.map((f, i) => (
          <div key={i} className="flex items-center gap-2.5 py-1.5">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0
              ${color === 'gold' ? 'bg-[#D4A94E]/20' : 'bg-blue-500/20'}`}>
              <Check className={`w-2.5 h-2.5 ${color === 'gold' ? 'text-[#D4A94E]' : 'text-blue-400'}`} />
            </div>
            <span className="text-gray-300 text-xs">{f}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={handleBuy}
        disabled={loading}
        className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
          font-bold text-sm transition-all duration-200
          ${loading ? 'opacity-60 cursor-wait' : 'hover:scale-[1.02] active:scale-100'}
          ${isAnnual
            ? 'bg-gradient-to-r from-[#D4A94E] to-amber-400 text-[#050810] shadow-lg shadow-[#D4A94E]/25'
            : color === 'gold'
              ? 'bg-[#D4A94E]/15 border border-[#D4A94E]/30 text-[#D4A94E] hover:bg-[#D4A94E]/25'
              : 'bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-blue-500/25'
          }`}>
        {loading
          ? <><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Processing…</>
          : <>{isAnnual ? <Sparkles className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />} Get Started — Pay Securely</>
        }
      </button>

      <p className="text-center text-gray-600 text-[10px] mt-3">
        🔒 Secure checkout via Stripe · AED pricing
      </p>
    </div>
  );
};

// ─── Testimonial card ────────────────────────────────────────────────────────
const Testimonial = ({ name, role, text, avatar }) => (
  <div className="p-6 rounded-2xl bg-white/[0.04] border border-white/[0.07] hover:border-white/15
    transition-all duration-300 flex flex-col">
    <div className="flex text-[#D4A94E] mb-4 gap-0.5">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
    </div>
    <p className="text-gray-300 text-sm leading-relaxed mb-5 flex-1">"{text}"</p>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4A94E]/30 to-[#D4A94E]/10
        flex items-center justify-center text-[#D4A94E] font-bold text-sm">
        {avatar}
      </div>
      <div>
        <p className="text-white font-semibold text-sm">{name}</p>
        <p className="text-gray-500 text-xs">{role}</p>
      </div>
    </div>
  </div>
);

// ─── FAQ Item ────────────────────────────────────────────────────────────────
const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-all duration-200 overflow-hidden
      ${open ? 'border-[#D4A94E]/30 bg-[#D4A94E]/5' : 'border-white/[0.07] bg-white/[0.02]'}`}>
      <button className="w-full flex items-center justify-between p-5 text-left gap-4"
        onClick={() => setOpen(o => !o)}>
        <span className="text-white font-medium text-sm">{q}</span>
        <div className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 transition-all
          ${open ? 'border-[#D4A94E]/40 bg-[#D4A94E]/10 rotate-45' : 'border-white/20'}`}>
          <span className="text-xs text-[#D4A94E] font-bold">+</span>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled,   setScrolled]   = useState(false);
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [period,     setPeriod]     = useState('annual'); // default to best-value

  const [heroRef,     heroVisible]    = useReveal(0.05);
  const [statsRef,    statsVisible]   = useReveal(0.3);
  const [crmRef,      crmVisible]     = useReveal(0.1);
  const [hunterRef,   hunterVisible]  = useReveal(0.1);
  const [pricingRef,  pricingVisible] = useReveal(0.05);
  const [socialRef,   socialVisible]  = useReveal(0.1);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  }, []);

  const navItems = [
    { label: 'Smart CRM',    id: 'crm'     },
    { label: 'Realty Hunter', id: 'hunter'  },
    { label: 'Pricing',      id: 'pricing' },
    { label: 'FAQ',          id: 'faq'     },
  ];

  const hunterFeatures = [
    'Unlimited searches during plan period',
    'Direct owner WhatsApp numbers — AI-verified',
    'Cross-referenced with DLD + Facebook data',
    'pHash image matching OSINT engine',
    'Private dashboard — only you see results',
    'Avg 3-minute owner lookup',
    'Bayut & Dubizzle support',
    'Cancel anytime — 14-day satisfaction guarantee',
  ];

  const crmFeatures = [
    'WhatsApp & Telegram AI bot — 24/7 auto-reply',
    'Full CRM pipeline with AI lead scoring',
    'Unlimited leads & conversations',
    'Real-time analytics & KPI dashboard',
    'Multi-language: EN / AR / FA / RU',
    'Follow-up automation & reminders',
    'Manual agent takeover (live chat)',
    'Cancel anytime — 14-day satisfaction guarantee',
  ];

  return (
    <div className="min-h-screen text-white overflow-x-hidden"
      style={{ background: '#050810', fontFamily: "'Inter', sans-serif" }}>

      {/* ── Grain overlay ── */}
      <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* ── NAVBAR ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500
        ${scrolled
          ? 'backdrop-blur-2xl border-b shadow-2xl shadow-black/40'
          : ''
        }`}
        style={{
          background: scrolled ? 'rgba(5,8,16,0.92)' : 'transparent',
          borderColor: scrolled ? 'rgba(255,255,255,0.06)' : 'transparent',
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-6">

          <div className="flex items-center gap-2.5 mr-auto">
            <img src="/goldlogo.svg" alt="Artin" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            <span className="font-black text-[15px] tracking-tight">
              <span className="text-white">ArtinSmart</span>
              <span style={{ color: '#D4A94E' }}>Realty</span>
            </span>
          </div>

          <div className="hidden lg:flex items-center gap-7 text-sm">
            {navItems.map(n => (
              <button key={n.id} onClick={() => scrollTo(n.id)}
                className="text-gray-400 hover:text-white transition-colors duration-200 font-medium">
                {n.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')}
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                text-gray-400 hover:text-white border border-white/[0.08] hover:border-white/20
                transition-all duration-200">
              Sign In
            </button>
            <button onClick={() => navigate('/login', { state: { mode: 'register' } })}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold
                transition-all duration-200 hover:scale-105 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #D4A94E, #f0c060)',
                color: '#050810',
                boxShadow: '0 4px 20px rgba(212,169,78,0.25)',
              }}>
              Start Free Trial
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button className="lg:hidden p-2 rounded-lg hover:bg-white/[0.08] transition-colors"
              onClick={() => setMenuOpen(o => !o)}>
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden px-4 pb-5 pt-2 space-y-1"
            style={{ background: 'rgba(5,8,16,0.97)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => scrollTo(n.id)}
                className="block w-full text-left py-3 px-3 rounded-lg text-gray-300 hover:text-white
                  hover:bg-white/[0.05] transition-all text-sm font-medium">
                {n.label}
              </button>
            ))}
            <button onClick={() => navigate('/login')}
              className="block w-full py-3 px-3 rounded-lg text-center text-sm font-semibold mt-3"
              style={{ background: 'rgba(212,169,78,0.15)', color: '#D4A94E', border: '1px solid rgba(212,169,78,0.2)' }}>
              Sign In to Dashboard
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* BG glow orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.06]"
            style={{ background: 'radial-gradient(circle, #D4A94E 0%, transparent 70%)' }} />
          <div className="absolute -top-1/4 -right-1/4 w-[600px] h-[600px] rounded-full opacity-[0.04]"
            style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
          <div className="absolute top-0 left-0 right-0 h-px opacity-20"
            style={{ background: 'linear-gradient(90deg, transparent, #D4A94E, transparent)' }} />
        </div>

        <div className={`max-w-5xl mx-auto px-4 sm:px-6 py-24 text-center relative z-10
          transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs font-semibold"
            style={{
              background: 'rgba(212,169,78,0.08)',
              border: '1px solid rgba(212,169,78,0.2)',
              color: '#D4A94E',
            }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Dubai's #1 Real Estate Intelligence Platform
            <span>·</span>
            200+ Active Agencies
          </div>

          {/* Headline */}
          <h1 className="font-black tracking-tight leading-[1.05] mb-7"
            style={{ fontSize: 'clamp(2.4rem, 7vw, 5.5rem)' }}>
            Stop Losing Deals<br />
            <span style={{
              background: 'linear-gradient(135deg, #D4A94E 0%, #f0c060 50%, #D4A94E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '200% auto',
            }}>
              to Faster Agents
            </span>
          </h1>

          {/* Sub */}
          <p className="text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
            Two AI-powered tools used by top Dubai agents.
            Close more deals with an AI CRM that never sleeps —
            and find the <strong className="text-white">direct owner's contact</strong> on any Bayut or Dubizzle listing in 3 minutes.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollTo('pricing')}
              className="group w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl
                font-bold transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #D4A94E, #f0c060)',
                color: '#050810',
                fontSize: '1.05rem',
                boxShadow: '0 8px 32px rgba(212,169,78,0.3)',
              }}>
              View Pricing
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            <button onClick={() => scrollTo('crm')}
              className="group w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl
                font-semibold transition-all duration-300 text-white hover:bg-white/[0.07]"
              style={{
                border: '1px solid rgba(255,255,255,0.12)',
                fontSize: '1.05rem',
              }}>
              <Play className="w-4 h-4" style={{ color: '#D4A94E' }} />
              See Live Demo
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-12 opacity-50">
            {[
              { icon: Shield, text: 'Bank-grade Encryption' },
              { icon: Award,  text: 'Dubai Registered' },
              { icon: Clock,  text: '24/7 AI Support' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-xs text-gray-400">
                <Icon className="w-3.5 h-3.5 text-gray-500" />
                {text}
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-5 h-5 text-gray-600" />
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} className="py-14 border-y" style={{ borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.015)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard value="200"  suffix="+" label="Active Agencies" active={statsVisible} />
            <StatCard value="98"   suffix="%" label="Bot Response Rate" active={statsVisible} />
            <StatCard value="45000" suffix="+" label="Owners Discovered" active={statsVisible} />
            <StatCard value="3"    suffix="min" label="Avg Owner Lookup" active={statsVisible} />
          </div>
        </div>
      </section>

      {/* ── CRM SECTION ── */}
      <section id="crm" ref={crmRef} className="py-20 sm:py-28">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-700
          ${crmVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold mb-6"
                style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa' }}>
                <Bot className="w-3.5 h-3.5" /> MODULE 01
              </div>
              <h2 className="font-black mb-4 leading-tight" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
                Artin Smart CRM
                <span className="block text-blue-400 font-semibold mt-1" style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)' }}>
                  AI-Powered Lead Management Platform
                </span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-sm sm:text-base">
                Your leads message you in Arabic at 2am. Without a system, they buy from someone else by 8am.
                <strong className="text-white"> Artin Smart CRM qualifies, scores, and follows up automatically</strong> —
                while you sleep, while you're in a meeting, while you're closing another deal.
              </p>
              <div className="space-y-0 mb-8">
                <Feat icon={Bot}           text="24/7 AI WhatsApp & Telegram conversations" color="blue" />
                <Feat icon={Users}         text="Full pipeline — New → Qualified → Closed" color="blue" />
                <Feat icon={BarChart3}     text="Real-time analytics & predictive lead scoring" color="blue" />
                <Feat icon={MessageSquare} text="Manual live agent takeover in one click" color="blue" />
                <Feat icon={Globe}         text="Multi-language: EN / AR / FA / RU" color="blue" />
                <Feat icon={TrendingUp}    text="Automatic follow-ups & viewing reminders" color="blue" />
              </div>
              <button onClick={() => scrollTo('pricing')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm
                  transition-all hover:bg-blue-500/25"
                style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#93c5fd' }}>
                See CRM Pricing <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                style={{ background: 'linear-gradient(135deg, #3b82f6, transparent)' }} />
              <div className="relative rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(59,130,246,0.2)', background: '#0a0f1e' }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] opacity-70"
                  style={{ background: 'linear-gradient(90deg, transparent, #3b82f6, #25d366, transparent)' }} />
                <div className="w-full h-[300px] sm:h-[420px] flex flex-col items-center justify-center group cursor-pointer relative" 
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1000)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-all duration-300 backdrop-blur-[2px]" />
                  <div className="relative z-10 w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_30px_rgba(220,38,38,0.5)] group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
                  </div>
                  <p className="relative z-10 mt-4 font-semibold text-white/90 tracking-wide text-sm bg-black/40 px-4 py-1.5 rounded-full backdrop-blur-md border border-white/10">CRM Demo Video (Coming Soon)</p>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold"
                  style={{ background: 'rgba(10,15,30,0.85)', border: '1px solid rgba(59,130,246,0.35)', color: '#60a5fa', backdropFilter: 'blur(8px)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  LIVE DEMO
                </div>
              </div>
              <p className="text-center text-gray-600 text-xs mt-3">
                ▶ Watch the AI bot qualify a WhatsApp lead in real time
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HUNTER SECTION ── */}
      <section id="hunter" ref={hunterRef} className="py-20 sm:py-28"
        style={{ background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-700
          ${hunterVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold mb-6"
                style={{ background: 'rgba(212,169,78,0.1)', border: '1px solid rgba(212,169,78,0.2)', color: '#D4A94E' }}>
                <Target className="w-3.5 h-3.5" /> MODULE 02
              </div>
              <h2 className="font-black mb-4 leading-tight" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
                Realty Hunter
                <span className="block font-semibold mt-1" style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)', color: '#D4A94E' }}>
                  Owner Contact Discovery Engine
                </span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed text-sm sm:text-base">
                Bayut and Dubizzle hide the owner's number on purpose — the agent gets the call, the agent gets the commission.
                <strong className="text-white"> You pay 2% on a AED 2M property = AED 40,000 to someone who made two phone calls.</strong>
                {' '}Realty Hunter finds the owner's direct WhatsApp in 3 minutes. Deal directly. Keep the commission.
              </p>
              <div className="space-y-0 mb-8">
                <Feat icon={Phone}  text="Direct, verified owner WhatsApp numbers" />
                <Feat icon={Shield} text="AI-verified: owner vs agency detection" />
                <Feat icon={MapPin} text="All Dubai districts + DLD cross-reference" />
                <Feat icon={Cpu}    text="pHash image matching + OSINT engine" />
                <Feat icon={Lock}   text="100% private — only you see your results" />
                <Feat icon={Zap}    text="Results in under 3 minutes, guaranteed" />
              </div>
              <button onClick={() => scrollTo('pricing')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm
                  transition-all hover:opacity-90"
                style={{
                  background: 'rgba(212,169,78,0.15)',
                  border: '1px solid rgba(212,169,78,0.3)',
                  color: '#D4A94E',
                }}>
                See Hunter Pricing <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl opacity-15 blur-2xl"
                style={{ background: 'linear-gradient(135deg, #D4A94E, transparent)' }} />
              <div className="relative rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(212,169,78,0.2)', background: '#0a0a0f' }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] opacity-70"
                  style={{ background: 'linear-gradient(90deg, transparent, #D4A94E, transparent)' }} />
                <div className="w-full h-[300px] sm:h-[420px] flex flex-col items-center justify-center group cursor-pointer relative" 
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1000)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-all duration-300 backdrop-blur-[2px]" />
                  <div className="relative z-10 w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_30px_rgba(220,38,38,0.5)] group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
                  </div>
                  <p className="relative z-10 mt-4 font-semibold text-white/90 tracking-wide text-sm bg-black/40 px-4 py-1.5 rounded-full backdrop-blur-md border border-white/10">Realty Hunter Demo Video (Coming Soon)</p>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold"
                  style={{ background: 'rgba(10,10,15,0.85)', border: '1px solid rgba(212,169,78,0.35)', color: '#D4A94E', backdropFilter: 'blur(8px)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  LIVE DEMO
                </div>
              </div>
              <p className="text-center text-gray-600 text-xs mt-3">
                ▶ Watch the bot extract a direct owner contact from a Bayut listing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" ref={pricingRef} className="py-20 sm:py-28">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-700
          ${pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>

          {/* Section header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-5"
              style={{ background: 'rgba(212,169,78,0.08)', border: '1px solid rgba(212,169,78,0.2)', color: '#D4A94E' }}>
              <Sparkles className="w-3.5 h-3.5" />
              TRANSPARENT PRICING — AED
            </div>
            <h2 className="font-black mb-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)' }}>
              Choose Your Plan
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-sm sm:text-base">
              All plans include a <strong className="text-white">14-day satisfaction guarantee</strong>.
              Pay securely via Stripe. Cancel anytime.
            </p>
          </div>

          {/* Period toggle */}
          <div className="flex justify-center mb-12">
            <div className="flex p-1 rounded-2xl gap-1"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {periods.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
                    ${period === p.key ? 'text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
                  style={period === p.key ? {
                    background: 'linear-gradient(135deg, #D4A94E, #f0c060)',
                    color: '#050810',
                  } : {}}>
                  {p.label}
                  {p.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap
                      text-[9px] font-black px-2 py-0.5 rounded-full bg-green-500 text-white shadow">
                      {p.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <PricingCard
              product="Realty Hunter"
              icon={Target}
              color="gold"
              period={period}
              prices={hunterPrices}
              features={hunterFeatures}
              badge={period === 'biannual' ? '2 MONTHS FREE' : period === 'annual' ? '3 MONTHS FREE' : null}
              navigate={navigate}
            />
            <PricingCard
              product="Artin Smart CRM"
              icon={Bot}
              color="blue"
              period={period}
              prices={crmPrices}
              features={crmFeatures}
              badge={period === 'biannual' ? '2 MONTHS FREE' : period === 'annual' ? '3 MONTHS FREE' : null}
              navigate={navigate}
            />
          </div>

          {/* Comparison note */}
          <div className="mt-10 max-w-4xl mx-auto rounded-2xl p-6"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center text-sm">
              {[
                { label: 'Realty Hunter Monthly', value: 'AED 500 / mo', sub: '— or —', compare: null },
                { label: '6-Month Plan', value: 'AED 2,000', sub: '≈ AED 333/mo · 2 months free', compare: null },
                { label: 'Annual Plan', value: 'AED 4,500', sub: '≈ AED 375/mo · 3 months free', compare: null },
              ].map(r => (
                <div key={r.label} className="py-2">
                  <p className="text-gray-500 text-xs mb-1">{r.label}</p>
                  <p className="font-black" style={{ color: '#D4A94E' }}>{r.value}</p>
                  <p className="text-gray-600 text-[11px] mt-1">{r.sub}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-gray-600 text-sm mt-8">
            Need both modules? Contact us for a{' '}
            <button onClick={() => navigate('/login')} style={{ color: '#D4A94E' }}
              className="hover:underline font-semibold">
              custom bundle price
            </button>
            {' '}→
          </p>
        </div>
      </section>

      {/* ── SOCIAL PROOF ── */}
      <section ref={socialRef} className="py-20 sm:py-24"
        style={{ background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-center font-black mb-12" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
            Trusted by Dubai's Top Agents
          </h2>
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-5 transition-all duration-700
            ${socialVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Testimonial
              avatar="A"
              name="Ahmad Al-Rashid"
              role="Senior Agent, Palm Realty"
              text="Realty Hunter found me a direct owner contact in 4 minutes. I saved AED 40,000 in commission on my first deal. This tool pays for itself 80× over."
            />
            <Testimonial
              avatar="E"
              name="Elena Sorokina"
              role="Director, Marina Properties"
              text="After deploying the AI CRM bot, our lead conversion jumped from 12% to 28% in 6 weeks. The bot handles Arabic replies at 3am better than any human agent."
            />
            <Testimonial
              avatar="O"
              name="Omar Shaikh"
              role="Broker, JBR Investments"
              text="I closed 3 direct-owner deals in one month that I would have lost to competing agents. The Hunter paid for 2 years of subscription in a single deal."
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-center font-black mb-12" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              {
                q: 'How does payment work?',
                a: 'All payments are processed securely via Stripe. We accept all major credit/debit cards. After payment, your account is activated instantly. For USDT (crypto) payments, contact support via Telegram.',
              },
              {
                q: 'Can I try before I buy?',
                a: 'Yes. Register for free and get a 14-day trial period. No credit card required to create your account. If you are not satisfied within 14 days, we offer a full refund.',
              },
              {
                q: 'What is the difference between 6-month and annual plans?',
                a: 'The 6-month plan gives you 2 free months (you pay for 4, get 6). The annual plan gives you 3 free months (you pay for 9, get 12). Both are billed as a single one-time payment — no recurring charges.',
              },
              {
                q: 'How does Realty Hunter find the owner number?',
                a: 'Our AI engine cross-references property listing data with public DLD (Dubai Land Department) records, Facebook business profiles, and image matching to identify whether a listing is from the owner or an agency, then extracts and verifies the direct contact via WhatsApp.',
              },
              {
                q: 'Is my data private?',
                a: 'Absolutely. Your search results are private and only visible to you. We do not share, sell, or expose your search history to other users or third parties. All data is encrypted at rest and in transit.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. Both monthly and multi-month plans can be cancelled without penalty. Multi-month plans are non-refundable after the 14-day guarantee window, but you retain access for the full paid period.',
              },
              {
                q: 'How do I register?',
                a: "Click 'Start Free Trial' at the top of this page. Fill in your name, company, email, and password. Your account is active immediately — no admin approval, no waiting.",
              },
            ].map(f => <FAQ key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="relative rounded-3xl p-10 sm:p-16 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(212,169,78,0.08), rgba(212,169,78,0.03))',
              border: '1px solid rgba(212,169,78,0.2)',
            }}>
            <div className="absolute inset-0 opacity-10"
              style={{ background: 'radial-gradient(ellipse at center top, #D4A94E, transparent 60%)' }} />
            <div className="relative z-10">
              <p className="text-xs font-bold tracking-widest mb-5 opacity-60" style={{ color: '#D4A94E' }}>
                LIMITED SLOTS PER DISTRICT
              </p>
              <h2 className="font-black mb-5 leading-tight" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)' }}>
                Ready to Dominate<br />
                <span style={{ color: '#D4A94E' }}>Dubai Real Estate?</span>
              </h2>
              <p className="text-gray-400 mb-10 text-sm sm:text-base max-w-lg mx-auto">
                Every day without this system, leads are going to agents who have it.
                Start your <strong className="text-white">14-day free trial today</strong>.
              </p>
              <button onClick={() => navigate('/login', { state: { mode: 'register' } })}
                className="group inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-black
                  transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #D4A94E, #f0c060)',
                  color: '#050810',
                  fontSize: '1.1rem',
                  boxShadow: '0 8px 40px rgba(212,169,78,0.35)',
                }}>
                Start Free Trial — No Card Required
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-gray-600 text-xs mt-5">
                14-day satisfaction guarantee · Secure checkout via Stripe
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: 'rgba(3,5,12,0.98)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-10">

            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/goldlogo.svg" alt="Artin" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                <span className="font-bold text-sm"><span className="text-white">ArtinSmart</span><span style={{ color: '#D4A94E' }}>Realty</span></span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-4">
                Dubai's #1 AI-powered real estate intelligence platform. Trusted by 200+ agencies across UAE.
              </p>
              <div className="flex gap-2">
                {[
                  { href: 'https://www.facebook.com/artinsmartagent', label: 'FB' },
                  { href: 'https://www.linkedin.com/company/artinsmartagent', label: 'LI' },
                  { href: 'https://instagram.com/artinsmartagent', label: 'IG' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold
                      text-gray-500 hover:text-white transition-all"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            {[
              {
                title: 'Products',
                links: [
                  { label: 'Smart CRM',    href: '#crm', scroll: true },
                  { label: 'Realty Hunter', href: '#hunter', scroll: true },
                  { label: 'SmartTrade',   href: 'https://trade.artinsmartagent.com' },
                  { label: 'All Products', href: 'https://artinsmartagent.com' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { label: 'About Us', href: 'https://artinsmartagent.com/about' },
                  { label: 'Pricing',  href: '#pricing', scroll: true },
                  { label: 'Contact',  href: 'https://artinsmartagent.com/contact' },
                  { label: 'Blog',     href: 'https://artinsmartagent.com/blog' },
                ],
              },
              {
                title: 'Legal',
                links: [
                  { label: 'Privacy Policy',   href: 'https://artinsmartagent.com/privacy' },
                  { label: 'Terms of Service', href: 'https://artinsmartagent.com/terms' },
                  { label: 'Refund Policy',    href: 'https://artinsmartagent.com/refunds' },
                ],
              },
            ].map(col => (
              <div key={col.title}>
                <p className="text-white font-semibold text-xs mb-3 uppercase tracking-wider">{col.title}</p>
                <ul className="space-y-2">
                  {col.links.map(l => (
                    <li key={l.label}>
                      {l.scroll
                        ? <button onClick={() => scrollTo(l.href.replace('#', ''))}
                            className="text-gray-500 hover:text-white text-xs transition-colors text-left">
                            {l.label}
                          </button>
                        : <a href={l.href} target={l.href.startsWith('http') ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className="text-gray-500 hover:text-white text-xs transition-colors">
                            {l.label}
                          </a>
                      }
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="text-gray-600 text-[11px] text-center sm:text-left">
              © 2025–2026 <span className="text-gray-400 font-medium">Artin SmartAgent Platform</span>
              {' '}· AMHR Marketing Management LLC · Dubai, UAE
            </p>
            <p className="text-gray-600 text-[11px] flex items-center gap-1">
              Powered by{' '}
              <a href="https://artinwebs.org" target="_blank" rel="noopener noreferrer"
                style={{ color: '#D4A94E' }} className="hover:underline font-medium">
                Artin Web
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
